import type { UseFormReturn } from 'react-hook-form';
import { AlertCircle, ShieldCheck } from 'lucide-react';
import { useMediaRealms } from '../../../../hooks/useMediaRealms';
import type { SipPeerFormData } from '../schema';
import { SRTP_CRYPTO_SUITES } from '../schema';

/** Media tab: media realm selection + SRTP/SDP media security. */
export function MediaTab({ form, profileLocked = false, profileName }: { form: UseFormReturn<SipPeerFormData>; profileLocked?: boolean; profileName?: string | null }) {
  const { data: mediaRealms, isLoading: mediaRealmsLoading } = useMediaRealms();
  const compatibleRealms = mediaRealms ?? [];

  const selectedRealmId = form.watch('mediaRealmId');
  const selectedRealm = mediaRealms?.find(r => r.id === selectedRealmId);
  const mediaRealmRegister = form.register('mediaRealmId');

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Media Configuration</h3>
        <p className="text-sm text-gray-600 mb-4">
          Select a media realm to control RTP port allocation, then configure media security (SRTP).
        </p>
      </div>

      {/* Media Realm Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Media Realm <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Required for media. The engine uses this realm to allocate RTP ports, and calls with media may be skipped or degraded until one is selected.
        </p>
        <select
          {...mediaRealmRegister}
          value={selectedRealmId ?? ''}
          onChange={(event) => {
            form.setValue('mediaRealmId', event.target.value || undefined, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={mediaRealmsLoading}
        >
          <option value="">
            {mediaRealmsLoading
              ? 'Loading media realms...'
              : 'Select media realm (required)'}
          </option>
          {compatibleRealms.map((realm) => (
            <option key={realm.id} value={realm.id}>
              {realm.name} ({realm.portRangeStart}-{realm.portRangeEnd})
            </option>
          ))}
        </select>
        {form.formState.errors.mediaRealmId && (
          <p className="mt-2 text-sm text-red-600 flex items-center">
            <AlertCircle className="w-4 h-4 mr-1" />
            {form.formState.errors.mediaRealmId.message}
          </p>
        )}
        {!selectedRealmId && (
          <p className="mt-2 text-sm text-amber-700 flex items-center">
            <AlertCircle className="w-4 h-4 mr-1" />
            No media realm selected. Media-capable calls will be skipped or degraded by the engine.
          </p>
        )}
        {compatibleRealms.length === 0 && !mediaRealmsLoading && (
          <p className="mt-2 text-sm text-amber-600 flex items-center">
            <AlertCircle className="w-4 h-4 mr-1" />
            No media realms found. Create a media realm in Network Settings before adding a peer.
          </p>
        )}
      </div>

      {/* Selected Realm Details */}
      {selectedRealm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-800 mb-2">Selected Media Realm Details</h4>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div>
              <dt className="text-blue-600">Name</dt>
              <dd className="font-medium text-blue-900">{selectedRealm.name}</dd>
            </div>
            <div>
              <dt className="text-blue-600">Port Range</dt>
              <dd className="font-medium text-blue-900">{selectedRealm.portRangeStart} - {selectedRealm.portRangeEnd}</dd>
            </div>
            {selectedRealm.description && (
              <div className="col-span-2">
                <dt className="text-blue-600">Description</dt>
                <dd className="text-blue-900">{selectedRealm.description}</dd>
              </div>
            )}
          </dl>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
        <div className="font-medium text-gray-900">Resolved media realm</div>
        <div>{selectedRealm ? `${selectedRealm.name} (${selectedRealm.portRangeStart}-${selectedRealm.portRangeEnd})` : 'None selected'}</div>
      </div>

      {/* Media Security (SRTP) — required: the engine drops peers without a valid SRTP policy */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <h4 className="font-medium text-gray-900 flex items-center">
          <ShieldCheck className="w-5 h-5 mr-2 text-gray-600" />
          Media Security (SRTP)
        </h4>
        {profileLocked && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Media policy is managed by attached profile {profileName ? <span className="font-medium">{profileName}</span> : null}.
          </div>
        )}
        <p className="text-xs text-gray-500">
          Controls SRTP/SDES (RFC 4568) media encryption for this peer. This is required —
          peers without a valid SRTP policy are skipped by the SIP engine and cannot pass media.
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SRTP policy</label>
          <select
            {...form.register('mediaConfig.srtp')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            disabled={profileLocked}
          >
            <option value="disabled">Disabled (RTP only)</option>
            <option value="optional">Optional (use SRTP when offered)</option>
            <option value="mandatory">Mandatory (require SRTP)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Crypto suites</label>
          <div className="space-y-2">
            {SRTP_CRYPTO_SUITES.map((suite) => {
              const selected = (form.watch('mediaConfig.cryptoSuites') ?? []) as string[];
              return (
                <label key={suite} className="flex items-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={selected.includes(suite)}
                    disabled={profileLocked}
                    onChange={(event) => {
                      const current = (form.getValues('mediaConfig.cryptoSuites') ?? []) as string[];
                      const next = event.target.checked
                        ? Array.from(new Set([...current, suite]))
                        : current.filter((item) => item !== suite);
                      form.setValue('mediaConfig.cryptoSuites', next, { shouldDirty: true });
                    }}
                  />
                  <span className="font-mono text-xs">{suite}</span>
                </label>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Offered in priority order. AES_CM_128_HMAC_SHA1_80 is the safe default.
          </p>
        </div>

        <label className="flex items-center text-sm text-gray-700">
          <input type="checkbox" className="mr-2" {...form.register('mediaConfig.expandCryptoSuites')} disabled={profileLocked} />
          Expand crypto suites (offer additional compatible suites in outbound SDP)
        </label>

        <label className="flex items-center text-sm text-gray-700">
          <input type="checkbox" className="mr-2" {...form.register('mediaConfig.sdpStripIce')} disabled={profileLocked} />
          Strip ICE candidates from SDP (for carriers that do not support ICE)
        </label>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Outbound SDP profile</label>
          <select
            {...form.register('mediaConfig.sdpOutboundProfile')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            disabled={profileLocked}
          >
            <option value="passthrough">Passthrough</option>
            <option value="carrier-basic-rtp">Carrier basic RTP</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Use carrier-basic-rtp for Telia-like carrier TLS/SRTP profiles that need normalized RTP SDP.
          </p>
        </div>
      </div>
    </div>
  );
}
